// HERE Geocoding & Search REST helpers for the address-input controllers.
//
// HERE has no drop-in autocomplete widget like google.maps.places.Autocomplete, so
// attachHereAutocomplete() builds a small debounced suggestion dropdown over the
// Autosuggest API. All calls are keyed by <meta name="here-api-key">.
import { hereApiKey } from "./here_maps_loader"

const GEOCODE_URL = "https://geocode.search.hereapi.com/v1/geocode"
const AUTOSUGGEST_URL = "https://autosuggest.search.hereapi.com/v1/autosuggest"
// Bias suggestions toward the DACH region (Germany centroid).
const DACH_AT = "51.1657,10.4515"
// Focus point per supported Land (TenantConfiguration::COUNTRIES). Autosuggest only BIASES
// toward it, unlike the geocode filter: a site across the border must stay pickable, and
// picking it is what switches the form's Land.
const COUNTRY_FOCUS = {
  de: DACH_AT,
  at: "47.5162,14.5501",
  ch: "46.8182,8.2275",
  lu: "49.8153,6.1296",
}
const DEBOUNCE_MS = 250

// HERE returns ISO 3166-1 alpha-3 country codes; the address forms store alpha-2.
const ALPHA3_TO_ALPHA2 = {
  DEU: "DE",
  AUT: "AT",
  CHE: "CH",
  FRA: "FR",
  ITA: "IT",
  LUX: "LU",
  BEL: "BE",
  NLD: "NL",
  POL: "PL",
  CZE: "CZ",
  DNK: "DK",
  LIE: "LI",
}

function alpha2(countryCode) {
  if (!countryCode) return ""
  return ALPHA3_TO_ALPHA2[countryCode] || countryCode.slice(0, 2)
}

function alpha3(countryCode) {
  if (!countryCode) return null
  const upcased = countryCode.toUpperCase()
  return Object.keys(ALPHA3_TO_ALPHA2).find((key) => ALPHA3_TO_ALPHA2[key] === upcased) || null
}

// Normalize a HERE result item's `address` into the fields the forms expect.
export function addressFields(item) {
  const a = item?.address || {}
  const street = [a.street, a.houseNumber].filter(Boolean).join(" ")
  return {
    street,
    city: a.city || "",
    postalCode: a.postalCode || "",
    country: alpha2(a.countryCode),
    label: a.label || item?.title || "",
    position: item?.position || null,
  }
}

async function fetchJson(url) {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HERE request failed: ${response.status}`)
  return response.json()
}

// Forward geocode a free-form address; resolves to the first result's fields (or null).
// An alpha-2 `country` restricts the search to that country (same filter the server-side
// geocode applies from a site's Land).
export async function hereGeocode(query, country = null) {
  const apikey = hereApiKey()
  if (!apikey || !query) return null
  const countryCode = alpha3(country)
  const filter = countryCode ? `&in=countryCode:${countryCode}` : ""
  const url = `${GEOCODE_URL}?q=${encodeURIComponent(query)}${filter}&lang=de-DE&limit=1&apiKey=${apikey}`
  const data = await fetchJson(url)
  const item = data.items?.[0]
  return item ? addressFields(item) : null
}

// `show=details` is what makes the structured address come back. Without it Autosuggest
// answers with `address: { label }` and nothing else — enough to render a suggestion and to
// drop a pin, but every consumer that fills separate Straße/PLZ/Ort/Land inputs would get
// empty strings and wipe the form instead of completing it.
async function hereAutosuggest(query, at = DACH_AT) {
  const apikey = hereApiKey()
  if (!apikey || !query) return []
  const url = `${AUTOSUGGEST_URL}?q=${encodeURIComponent(query)}&at=${at}&lang=de-DE&limit=5&show=details&apiKey=${apikey}`
  const data = await fetchJson(url)
  // Keep only items that carry a resolvable position (streets/houses/places).
  return (data.items || []).filter((item) => item.position)
}

const PIN_SVG = `<svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
</svg>`

const ROW_CLASS = "flex cursor-pointer items-start gap-2 px-3 py-2"
const ROW_ACTIVE_CLASS = "bg-green-50"

let listSequence = 0

// The full address is the useful second line, but locality results repeat the title there.
function secondaryLabel(item) {
  const label = item.address?.label || ""
  return label && label !== item.title ? label : ""
}

// HERE Autosuggest already tells us which parts of the title matched the query — emphasise
// exactly those, the way google.maps.places.Autocomplete used to.
function titleFragment(item) {
  const title = item.title || item.address?.label || ""
  const fragment = document.createDocumentFragment()
  let cursor = 0

  ;(item.highlights?.title || []).forEach(({ start, end }) => {
    if (start > cursor) fragment.append(title.slice(cursor, start))
    const match = document.createElement("span")
    match.className = "font-semibold text-green-700"
    match.textContent = title.slice(start, end)
    fragment.append(match)
    cursor = end
  })

  if (cursor < title.length) fragment.append(title.slice(cursor))
  return fragment
}

// Wire a debounced suggestion dropdown onto an <input>. `onSelect(fields, item)` is
// called when the user picks a suggestion. Returns a teardown function.
// `country` is an optional getter for the alpha-2 Land to focus suggestions on.
export function attachHereAutocomplete(input, { onSelect, country = () => null }) {
  const listId = `here-autocomplete-${(listSequence += 1)}`

  const list = document.createElement("ul")
  list.id = listId
  list.setAttribute("role", "listbox")
  list.className =
    "here-autocomplete absolute left-0 top-full z-50 mt-1 hidden max-h-72 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5"

  // Anchor the dropdown to the input itself, not to input.parentElement: on the site form that
  // parent is a flex row (input + address-book button), and an absolutely positioned child of a
  // flex container is placed at the container's content-box origin — i.e. on top of the input.
  //
  // Wrap at most once, and never unwrap (see the teardown). maps-autocomplete binds its Stimulus
  // controller to the input itself, and Stimulus reads a move of a controller element as a
  // remove-then-add: re-parenting it fires disconnect() + connect(), which re-enters this
  // function. Moving the input on every pass would loop forever and hang the tab.
  let wrapper = input.parentElement
  if (!(wrapper && "hereAutocompleteWrapper" in wrapper.dataset)) {
    wrapper = document.createElement("div")
    wrapper.dataset.hereAutocompleteWrapper = ""
    wrapper.className = "relative min-w-0 flex-1"
    input.parentElement.insertBefore(wrapper, input)
    wrapper.append(input)
  }
  // A Turbo-cached wrapper can still hold the previous list; appending ours never moves the input.
  wrapper.querySelector(":scope > .here-autocomplete")?.remove()
  wrapper.append(list)

  input.setAttribute("autocomplete", "off")
  input.setAttribute("role", "combobox")
  input.setAttribute("aria-autocomplete", "list")
  input.setAttribute("aria-expanded", "false")
  input.setAttribute("aria-controls", listId)

  let timer = null
  let items = []
  let activeIndex = -1

  const hide = () => {
    list.classList.add("hidden")
    list.innerHTML = ""
    input.setAttribute("aria-expanded", "false")
    input.removeAttribute("aria-activedescendant")
    items = []
    activeIndex = -1
  }

  const setActive = (index) => {
    const rows = list.children
    if (activeIndex >= 0 && rows[activeIndex]) {
      rows[activeIndex].classList.remove(ROW_ACTIVE_CLASS)
      rows[activeIndex].setAttribute("aria-selected", "false")
    }

    activeIndex = index
    const row = rows[activeIndex]
    if (!row) return

    row.classList.add(ROW_ACTIVE_CLASS)
    row.setAttribute("aria-selected", "true")
    row.scrollIntoView({ block: "nearest" })
    input.setAttribute("aria-activedescendant", row.id)
  }

  const buildRow = (item, index) => {
    const row = document.createElement("li")
    row.id = `${listId}-option-${index}`
    row.setAttribute("role", "option")
    row.setAttribute("aria-selected", "false")
    row.className = ROW_CLASS
    row.title = item.address?.label || item.title || ""

    const icon = document.createElement("span")
    icon.className = "mt-0.5 shrink-0 text-gray-400"
    icon.innerHTML = PIN_SVG

    const text = document.createElement("span")
    text.className = "min-w-0 flex-1"

    const primary = document.createElement("span")
    primary.className = "block break-words text-sm font-medium text-gray-900"
    primary.append(titleFragment(item))
    text.append(primary)

    const secondary = secondaryLabel(item)
    if (secondary) {
      const sub = document.createElement("span")
      sub.className = "block break-words text-xs text-gray-500"
      sub.textContent = secondary
      text.append(sub)
    }

    row.append(icon, text)
    row.addEventListener("mouseenter", () => setActive(index))
    // mousedown, not click: the input's blur would tear the list down first.
    row.addEventListener("mousedown", (event) => {
      event.preventDefault()
      choose(index)
    })
    return row
  }

  const render = (suggestions) => {
    if (!suggestions.length) {
      hide()
      return
    }

    items = suggestions
    activeIndex = -1
    list.innerHTML = ""
    suggestions.forEach((item, index) => list.appendChild(buildRow(item, index)))
    list.classList.remove("hidden")
    input.setAttribute("aria-expanded", "true")
  }

  const choose = (index) => {
    const item = items[index]
    if (!item) return
    onSelect(addressFields(item), item)
    hide()
  }

  const onInput = () => {
    clearTimeout(timer)
    const query = input.value.trim()
    if (query.length < 3) {
      hide()
      return
    }
    timer = setTimeout(async () => {
      try {
        const focus = COUNTRY_FOCUS[(country() || "").toLowerCase()] || DACH_AT
        render(await hereAutosuggest(query, focus))
      } catch (error) {
        console.error("HERE autosuggest failed", error)
        hide()
      }
    }, DEBOUNCE_MS)
  }

  const onKeydown = (event) => {
    if (list.classList.contains("hidden") || !items.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        setActive((activeIndex + 1) % items.length)
        break
      case "ArrowUp":
        event.preventDefault()
        setActive((activeIndex - 1 + items.length) % items.length)
        break
      case "Enter":
        if (activeIndex >= 0) {
          event.preventDefault()
          choose(activeIndex)
        }
        break
      case "Escape":
        hide()
        break
    }
  }

  const onBlur = () => setTimeout(hide, 150)

  input.addEventListener("input", onInput)
  input.addEventListener("keydown", onKeydown)
  input.addEventListener("blur", onBlur)

  return () => {
    clearTimeout(timer)
    input.removeEventListener("input", onInput)
    input.removeEventListener("keydown", onKeydown)
    input.removeEventListener("blur", onBlur)
    ;[
      "role",
      "aria-autocomplete",
      "aria-expanded",
      "aria-controls",
      "aria-activedescendant",
    ].forEach((attribute) => input.removeAttribute(attribute))
    // Leave the wrapper in place. Putting the input back would re-parent it — for a controller
    // bound to the input, that is exactly the mutation that re-invokes connect().
    list.remove()
  }
}
